import { createRootRoute, createRoute, createRouter, Outlet } from "@tanstack/react-router";
import { Shell } from "./components/Shell";
import { Empty } from "./components/ui";
import { SessionProvider, useSessionQuery } from "./lib/session";
import { LoginPage, NoAccessPage } from "./pages/auth";
import { AppsPage, MyDayPage } from "./pages/home";
import { OrganisationPage } from "./pages/admin/organisation";
import { GrantsPage, PeoplePage, PersonPage, RolesPage } from "./pages/admin/access";
import { AuditPage, EventsPage } from "./pages/admin/system";
import { InboxPage } from "./pages/inbox";
import { IntegrationsPage } from "./pages/integrations";
import { CrewBoardPage, PlannerPage } from "./pages/planner";
import { ProjectPage, ProjectsPage } from "./pages/projects";
import { PulsePage, WorkspacePage, WorkspacesPage } from "./pages/insights";
import { TeamPage, TeamsPage } from "./pages/teams";
import { ItemPage, WorkListPage } from "./pages/work";
import { ApprovalPage, ApprovalSettingsPage, ApprovalsPage, AssetsPage, InspectionRunPage, InspectionsPage, WorkflowPage, WorkflowsPage } from "./pages/phase3";

function Gate() {
  const s = useSessionQuery();
  if (s.isLoading) return <Empty>Loading ONE…</Empty>;
  if (!s.data?.authenticated) {
    if (window.location.pathname !== "/login") window.location.href = `/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`; // keep ?text= etc. (Share to ONE)
    return null;
  }
  if (!s.data.access) return <NoAccessPage name={s.data.person?.name} />;
  return (
    <SessionProvider value={s.data}>
      <Shell />
    </SessionProvider>
  );
}

import { SettingsPage } from "./pages/settings";
import { NotesPage } from "./pages/notes";
import { TriagePage } from "./pages/triage";
import { PlanPage } from "./pages/plan";
import { PlanSortPage } from "./pages/plan-sort";
import { PlanWeekPage } from "./pages/plan-week";
import { KnowledgePage } from "./pages/knowledge";
import { ReviewPage } from "./pages/review";
import { SharePage } from "./pages/share";
import { DigestPage } from "./pages/digest";
import { WealthPage } from "./pages/wealth";
import { DocumentsPage } from "./pages/documents";
import { MailPage } from "./pages/mail";
import { AssistantPage } from "./pages/assistant";
import { AssistantSettingsPage } from "./pages/assistant-settings";
import { InspectionSchedulesPage } from "./pages/admin/schedules";
const root = createRootRoute({ component: Outlet });
const login = createRoute({ getParentRoute: () => root, path: "/login", component: LoginPage });
const app = createRoute({ getParentRoute: () => root, id: "app", component: Gate });

const home = createRoute({ getParentRoute: () => app, path: "/", component: MyDayPage });
const inspSchedules = createRoute({ getParentRoute: () => app, path: "/admin/inspection-schedules", component: InspectionSchedulesPage });
const assistant = createRoute({ getParentRoute: () => app, path: "/assistant", component: AssistantPage });
const assistantSettings = createRoute({ getParentRoute: () => app, path: "/assistant/settings", component: AssistantSettingsPage });
const assistantConv = createRoute({ getParentRoute: () => app, path: "/assistant/$id", component: AssistantPage });
const mailPage = createRoute({ getParentRoute: () => app, path: "/mail", component: MailPage });
const notes = createRoute({ getParentRoute: () => app, path: "/notes", component: NotesPage });
const triage = createRoute({ getParentRoute: () => app, path: "/triage", component: TriagePage });
const plan = createRoute({ getParentRoute: () => app, path: "/plan", component: PlanPage });
const planSort = createRoute({ getParentRoute: () => app, path: "/plan/sort", component: PlanSortPage });
const planWeek = createRoute({
  getParentRoute: () => app,
  path: "/plan/week",
  component: PlanWeekPage,
  validateSearch: (s: Record<string, unknown>): { week?: string } => (typeof s.week === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s.week) ? { week: s.week } : {}),
});
const share = createRoute({ getParentRoute: () => app, path: "/share", component: SharePage });
const digest = createRoute({ getParentRoute: () => app, path: "/digest/$id", component: DigestPage });
const review = createRoute({ getParentRoute: () => app, path: "/review", component: ReviewPage });
const knowledge = createRoute({ getParentRoute: () => app, path: "/knowledge", component: KnowledgePage });
const wealth = createRoute({ getParentRoute: () => app, path: "/wealth", component: WealthPage });
const documents = createRoute({ getParentRoute: () => app, path: "/documents", component: DocumentsPage });
const documentOpen = createRoute({ getParentRoute: () => app, path: "/documents/$id", component: DocumentsPage });
const settings = createRoute({ getParentRoute: () => app, path: "/settings", component: SettingsPage });
const apps = createRoute({ getParentRoute: () => app, path: "/apps", component: AppsPage });
const org = createRoute({ getParentRoute: () => app, path: "/admin/organisation", component: OrganisationPage });
const people = createRoute({ getParentRoute: () => app, path: "/admin/people", component: PeoplePage });
const person = createRoute({ getParentRoute: () => app, path: "/admin/people/$id", component: PersonPage });
const roles = createRoute({ getParentRoute: () => app, path: "/admin/roles", component: RolesPage });
const grants = createRoute({ getParentRoute: () => app, path: "/admin/grants", component: GrantsPage });
const audit = createRoute({ getParentRoute: () => app, path: "/admin/audit", component: AuditPage });
const events = createRoute({ getParentRoute: () => app, path: "/admin/events", component: EventsPage });

const inbox = createRoute({ getParentRoute: () => app, path: "/inbox", component: InboxPage });
const workList = createRoute({ getParentRoute: () => app, path: "/work", component: WorkListPage });
const workItem = createRoute({ getParentRoute: () => app, path: "/work/$id", component: ItemPage });

const approvals = createRoute({ getParentRoute: () => app, path: "/approvals", component: ApprovalsPage });
const approval = createRoute({ getParentRoute: () => app, path: "/approvals/$id", component: ApprovalPage });
const workflows = createRoute({ getParentRoute: () => app, path: "/workflows", component: WorkflowsPage });
const workflow = createRoute({ getParentRoute: () => app, path: "/workflows/$id", component: WorkflowPage });
const inspections = createRoute({ getParentRoute: () => app, path: "/inspections", component: InspectionsPage });
const inspection = createRoute({ getParentRoute: () => app, path: "/inspections/$id", component: InspectionRunPage });
const assets = createRoute({ getParentRoute: () => app, path: "/assets", component: AssetsPage });
const aprSettings = createRoute({ getParentRoute: () => app, path: "/admin/approvals", component: ApprovalSettingsPage });

const integrations = createRoute({ getParentRoute: () => app, path: "/admin/integrations", component: IntegrationsPage });

const planner = createRoute({ getParentRoute: () => app, path: "/planner", component: PlannerPage });
const crew = createRoute({ getParentRoute: () => app, path: "/crew-board", component: CrewBoardPage });

const projects = createRoute({ getParentRoute: () => app, path: "/projects", component: ProjectsPage });
const project = createRoute({ getParentRoute: () => app, path: "/projects/$id", component: ProjectPage });

const workspaces = createRoute({ getParentRoute: () => app, path: "/workspaces", component: WorkspacesPage });
const workspace = createRoute({ getParentRoute: () => app, path: "/workspaces/$code", component: WorkspacePage });
const teams = createRoute({ getParentRoute: () => app, path: "/teams", component: TeamsPage });
const team = createRoute({ getParentRoute: () => app, path: "/teams/$id", component: TeamPage });
const pulse = createRoute({ getParentRoute: () => app, path: "/pulse", component: PulsePage });

const routeTree = root.addChildren([
  login,
  app.addChildren([home, inbox, workList, workItem, approvals, approval, workflows, workflow, inspections, inspection, assets, aprSettings, integrations, planner, crew, projects, project, workspaces, workspace, teams, team, pulse, apps, notes, knowledge, wealth, documents, documentOpen, triage, plan, planSort, planWeek, review, share, digest, settings, mailPage, assistant, assistantSettings, assistantConv, inspSchedules, org, people, person, roles, grants, audit, events]),
]);

export const router = createRouter({ routeTree, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
